import AgentUtil from "../AgentOverview/AgentUtil";
import { GenericResponse, LeagueListItem } from "../CreateRound/CreateRoundModels";
import CreateRoundUtil from "../CreateRound/CreateRoundUtil";
import InvokeApiMethods from "../ExternalApi/ExternalApiModels";
import { BaseResponse } from "../FixtureOverview/FixtureOverviewModels";
import { ListResponse } from "../api/ApiNgModels";
import ApiNgUtil from "../api/ApiNgUtil";
import { ExtLeague, ExtLeagueFilter, GetLeaguesRequest, League, LeagueFilter, LeagueNameParts, LeagueStat, MapLeagueRequest, Sport } from "./LeagueModels";

class LeagueUtil {
    static TEST_PROVIDER = "test";
    static PROVIDER_IDS : string[] = [LeagueUtil.TEST_PROVIDER];

    static async getLeagues(pageSize: number, lastId: number, filter? : LeagueFilter) : Promise<GenericResponse<ListResponse<League>>> {
        var urlParams = AgentUtil.getUrlParams(pageSize, undefined, undefined, lastId, filter);
        var url = `leagues?${urlParams}`;
        var listResponse = await ApiNgUtil.getListByUrl<League>(url, "leagues");
        if (!listResponse.success) {
            return listResponse;
        }
        var leagues : League[] = [] 
        if (listResponse.item?.items) {
            leagues = listResponse.item?.items;
        }
        if (leagues) {
            for (var i = 0; i < leagues.length; i++) {
                var league = leagues[i];
                LeagueUtil.fixLeague(league);
            }
        }
        return listResponse;
    }
    static toLeagueListItem(league: League) : LeagueListItem {
        return {
            id: `${league.id}`,
            rawName: league.name,
            name: league.nameWithoutLocation ?? league.name,
            title: league.title ?? league.name,
            location: league.location ?? ""
        };
    }
    static async getLeagueListItems(pageSize: number = 100, lastId: number = 0, q : string | undefined = undefined) : Promise<GenericResponse<LeagueListItem[]>> {
        var filter : LeagueFilter = {
            q: q && q.length > 0 ? q : undefined
        };
        var resp = await LeagueUtil.getLeagues(pageSize, lastId, filter);
        if (!resp.success) {
            return CreateRoundUtil.createGenericResponse<LeagueListItem[]>(false, resp.message, null);
        }
        var leagues = resp.item?.items;
        if (!leagues) {
            return CreateRoundUtil.createGenericResponse<LeagueListItem[]>(true, resp.message, null);
        }
        var items = leagues.map(LeagueUtil.toLeagueListItem);
        CreateRoundUtil.sortLeagues(items);
        return CreateRoundUtil.createGenericResponse<LeagueListItem[]>(true, "", items);
    }
    static async getLeagueByName(leagueName : string) : Promise<GenericResponse<LeagueListItem>> {
        const METHOD_NAME = "LeagueUtil.getLeagueByName";
        try {
            // The ng endpoint only supports a name pattern (q), so match exactly on the client.
            var resp = await LeagueUtil.getLeagues(100, 0, { q: leagueName });
            if (!resp.success) {
                return CreateRoundUtil.createGenericResponse<LeagueListItem>(false, resp.message, null);
            }
            var league = resp.item?.items?.find(it => it.name === leagueName);
            if (!league) {
                return CreateRoundUtil.createGenericResponse<LeagueListItem>(false, "League not found", null);
            }
            return CreateRoundUtil.createGenericResponse<LeagueListItem>(true, "", LeagueUtil.toLeagueListItem(league));
        } catch (error) {
            console.error(METHOD_NAME + " error!", error);
            return CreateRoundUtil.createGenericResponse<LeagueListItem>(false, METHOD_NAME + " error: " + error, null);
        }
    }
    static async updateLeague(id: number, updateJson: string) : Promise<GenericResponse<League>> {
        var resource = `leagues/${id}`;
        var apiResponse = await ApiNgUtil.invokeApi2(resource, InvokeApiMethods.PATCH(), updateJson);
        if (!apiResponse.success) {
            return CreateRoundUtil.createGenericResponse<League>(false, apiResponse.message, null);
        }
        var league : League | null = null; 
        if (apiResponse.json) {
            league = apiResponse.json;
        }
        if (league) {
            LeagueUtil.fixLeague(league);
        }
        return CreateRoundUtil.createGenericResponse<League>(true, apiResponse.message, league);
    }
    static async syncLeague(id: number) : Promise<GenericResponse<number>> {
        var resource = `leagues/${id}/sync`;
        var apiResponse = await ApiNgUtil.invokeApi2(resource, InvokeApiMethods.POST(), undefined);
        if (!apiResponse.success) {
            return CreateRoundUtil.createGenericResponse<number>(false, apiResponse.message, null);
        }
        var success = false;
        var message = "";
        if (apiResponse.json) {
            var resp = apiResponse.json;
            success = resp.success;
            if (resp.message) {
                message = resp.message;
            }
            if (success === true) {
                return CreateRoundUtil.createGenericResponse<number>(true, message, null);
            } else {
                if (message && message.length > 0) {
                    return CreateRoundUtil.createGenericResponse<number>(false, message, null);
                } else {
                    return CreateRoundUtil.createGenericResponse<number>(false, "Sync failed without message.", null);
                }
            }
        }
        return CreateRoundUtil.createGenericResponse<number>(false, "Sync invoked without response.", null);
    }
    static getLeagueNameParts(leagueName: string | undefined | null) : LeagueNameParts | undefined {
        if (!leagueName || leagueName.length <= 0) {
            return undefined;
        }
        var leagueNameWithoutLocation = leagueName;
        var leagueLocation = "";
        var leagueTitle = leagueName;
        var match = leagueName.match(CreateRoundUtil.leagueFormatPattern);
        if (match) {
            var leagueNameWithoutLocation = match.groups?.name ?? leagueName;
            leagueLocation = match.groups?.location ?? "";
            if (leagueNameWithoutLocation !== leagueName && leagueLocation.length > 0) {
                leagueTitle = leagueLocation + " - " + leagueNameWithoutLocation;
            }
        }

        return {
            name: leagueName,
            location: leagueLocation,
            nameWithoutLocation: leagueNameWithoutLocation,
            title: leagueTitle
        };
    }

    static fixLeague(league: League) {
        if (!league) {
            return;
        }
        league.title = league.name;
        league.location = "";
        league.nameWithoutLocation = league.name;
        if (league.countryName && league.countryName.length > 0) {
            // The ng endpoint returns the country separately, so prefer it over parsing the name.
            league.location = league.countryName;
            league.title = league.countryName + " - " + league.name;
            if (league.sportId && league.sportName && league.sportName.length > 0) {
                league.title = league.title + " (" + league.sportName + ")";
            }
            return;
        }
        var parts = LeagueUtil.getLeagueNameParts(league.name);
        if (!parts) {
            return;
        }
        league.title = parts.title;
        league.location = parts.location;
        league.nameWithoutLocation = parts.nameWithoutLocation;

        // var leagueName: string = league.name;
        // var leagueLocation = "";
        // var leagueTitle = league.name;
        // var match = leagueName.match(CreateRoundUtil.leagueFormatPattern);
        // if (match) {
        //     leagueName = match.groups?.name ?? league.name;
        //     leagueLocation = match.groups?.location ?? "";
        //     if (leagueName !== league.name && leagueLocation.length > 0) {
        //         leagueTitle = leagueLocation + " - " + leagueName;
        //     }
        // }
        // league.title = leagueTitle;
        // league.location = leagueLocation;
        // league.nameWithoutLocation= leagueName;        
    }
    // static fixLeagueProps(leagueMapping: LeagueMapping) {
    //     if (!leagueMapping) {
    //         return;
    //     }
    //     var parts = LeagueUtil.getLeagueNameParts(leagueMapping.leagueName);
    //     if (!parts) {
    //         return;
    //     }
    //     leagueMapping.leagueTitle = parts.title;
    //     leagueMapping.leagueLocation = parts.location;
    //     leagueMapping.leagueNameWithoutLocation = parts.nameWithoutLocation;        
    //     // var leagueName: string = leagueMapping.leagueName;
    //     // var leagueLocation = "";
    //     // var leagueTitle = leagueMapping.leagueName;
    //     // var match = leagueName.match(CreateRoundUtil.leagueFormatPattern);
    //     // if (match) {
    //     //     leagueName = match.groups?.name ?? leagueMapping.leagueName;
    //     //     leagueLocation = match.groups?.location ?? "";
    //     //     if (leagueName !== leagueMapping.leagueName && leagueLocation.length > 0) {
    //     //         leagueTitle = leagueLocation + " - " + leagueName;
    //     //     }
    //     // }
    //     // leagueMapping.leagueTitle = leagueTitle;
    //     // leagueMapping.leagueLocation = leagueLocation;
    //     // leagueMapping.leagueNameWithoutLocation= leagueName;        
    // }
    static async getLeagueStat() : Promise<GenericResponse<LeagueStat>> {
        var response = await ApiNgUtil.getObject<LeagueStat>("leagues/stat");
        return response;
    }
    // private static async getLeagueMappingsImpl(url: string) {
    //     var listResponse = await ApiNgUtil.getListByUrl<LeagueMapping>(url, "leagueMappings");
    //     if (!listResponse.success) {
    //         return listResponse;
    //     }
    //     var items : LeagueMapping[] = [] 
    //     if (listResponse.item?.items) {
    //         items = listResponse.item?.items;
    //     }
    //     if (items) {
    //         for (var i = 0; i < items.length; i++) {
    //             var item = items[i];
    //             LeagueUtil.fixLeagueProps(item);
    //         }
    //     }
    //     return listResponse;

    // }
    // static async getLeagueMappingsByLeagueId(leagueId: number) : Promise<GenericResponse<ListResponse<LeagueMapping>>> {
    //     var url = `leagues/${leagueId}/mappings`;
    //     var response = await LeagueUtil.getLeagueMappingsImpl(url);
    //     return response;
    //     // var listResponse = await ApiNgUtil.getListByUrl<LeagueMapping>(url, "leagueMappings");
    //     // if (!listResponse.success) {
    //     //     return listResponse;
    //     // }
    //     // var items : LeagueMapping[] = [] 
    //     // if (listResponse.item?.items) {
    //     //     items = listResponse.item?.items;
    //     // }
    //     // if (items) {
    //     //     for (var i = 0; i < items.length; i++) {
    //     //         var item = items[i];
    //     //         LeagueUtil.fixLeagueProps(item);
    //     //     }
    //     // }
    //     // return listResponse;
    // }
    // static async getLeagueMappings(pageSize: number, from: Date| undefined, to: Date| undefined, lastId: number, filter?: LeagueMappingFilter) : Promise<GenericResponse<ListResponse<LeagueMapping>>> {
    //     var urlParams = AgentUtil.getUrlParams(pageSize, from, to, lastId, filter);
    //     var url = `leagues/mappings?${urlParams}`;
    //     var response = await LeagueUtil.getLeagueMappingsImpl(url);
    //     return response;
    // }
    static async getExtLeagues(pageSize: number, lastId: number, filter? : ExtLeagueFilter) : Promise<GenericResponse<ListResponse<ExtLeague>>> {
        var urlParams = AgentUtil.getUrlParams(pageSize, undefined, undefined, lastId, filter);
        var url = `extLeagues?${urlParams}`;
        var listResponse = await ApiNgUtil.getListByUrl<ExtLeague>(url, "extLeagues");
        if (!listResponse.success) {
            return listResponse;
        }
        var items : ExtLeague[] = [] 
        if (listResponse.item?.items) {
            items = listResponse.item?.items;
        }
        if (items) {
            for (var i = 0; i < items.length; i++) {
                var item = items[i];
                LeagueUtil.fixExtLeague(item);
            }
        }
        return listResponse;
    }
    static async mapExtLeague(request: MapLeagueRequest) : Promise<GenericResponse<ExtLeague>> {
        const METHOD_NAME = "LeagueUtil.mapExtLeague";
        var url = `leagues/${request.leagueId}/map`;
        var saveJson = JSON.stringify(request);
        try {
            var resp = await ApiNgUtil.getObjectWithBody<ExtLeague>(url, InvokeApiMethods.POST(), saveJson);
            LeagueUtil.fixExtLeague(resp.item);
            return resp;
        } catch (error) {
            console.error(METHOD_NAME + " error.", error);
            return CreateRoundUtil.createGenericResponse<ExtLeague>(false, METHOD_NAME + " error: " + error, null);
        }
    }
    static async unmapLeague(mappingId: number) : Promise<GenericResponse<boolean>> {
        const METHOD_NAME = "LeagueUtil.unmapLeague";
        var url = `leagues/mappings/${mappingId}`;
        try {
            var resp = await ApiNgUtil.getObjectWithMethod<BaseResponse>(url, InvokeApiMethods.DELETE());
            if (resp.success) {
                if (resp.item) {
                    const success : boolean = (resp.item.success && resp.item.success === true);
                    return CreateRoundUtil.createGenericResponse(success, resp.item.message, success);
                } 
                return CreateRoundUtil.createGenericResponse(false, "No response", false);
            }
            return CreateRoundUtil.createGenericResponse(false, resp.message, false);
        } catch (error) {
            console.error(METHOD_NAME + " error.", error);
            return CreateRoundUtil.createGenericResponse<boolean>(false, METHOD_NAME + " error: " + error, false);
        }
    }

    // static async getProviderIds() : string[] {
    //     var providerIds : string[] = [];
    //     for (const providerId of LeagueUtil.PROVIDER_IDS) {
    //         providerIds.push(providerId);
    //     }
    //     return providerIds;
    // }
    static async getProviderIds() : Promise<GenericResponse<ListResponse<string>>> {
        // for (const providerId of LeagueUtil.PROVIDER_IDS) {
        //     providerIds.push(providerId);
        // }
        // return providerIds;
        var url = `providers`;
        var listResponse = await ApiNgUtil.getListByUrl<string>(url, "providers");
        return listResponse;
    }
    static async getSports() : Promise<GenericResponse<ListResponse<Sport>>> {
        var url = `sports`;
        var listResponse = await ApiNgUtil.getListByUrl<Sport>(url, "sports");
        return listResponse;
    }

    private static fixExtLeague(item: ExtLeague | undefined | null) : void {
        if (!item?.mapping) {
            return;
        }
        item.mappingId = item.mapping.id;
        item.leagueId = item.mapping.leagueId;
        item.leagueName = item.mapping.leagueName;
        if (item.mappingId && item.mappingId > 0) {
            item.mappingStatus = "REGISTERED";
        } else {
            item.mappingStatus = "OTHER_MATCH";
        }
    }
}
export default LeagueUtil;